/**
 * Formulario para crear/editar recetas complejas con cálculo nutricional
 */
import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Button,
  Space,
  message,
  Steps,
  Select,
  Table,
  Popconfirm,
  Typography,
  Tooltip,
  Alert,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useBranch } from "../../context/BranchContext";
import api from "../../api/endpoints";

const { Text, Paragraph } = Typography;

const { Step } = Steps;
const { TextArea } = Input;

const RecipeForm = ({ visible, recipe, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [ingredients, setIngredients] = useState([]);
  const [availableIngredients, setAvailableIngredients] = useState([]);
  const { activeBranch, branches } = useBranch();

  useEffect(() => {
    if (!visible) return;

    // Función para cargar ingredientes disponibles
    const loadAvailableIngredients = async () => {
      try {
        const ingredientsList = await api.recipes.getNutritionalIngredients(
          activeBranch
        );
        setAvailableIngredients(ingredientsList);
      } catch (error) {
        console.error("Error cargando ingredientes:", error);
      }
    };

    // Función para cargar ingredientes de la receta
    const loadRecipeIngredients = async () => {
      if (!recipe) return;
      try {
        const recipeIngredients = await api.recipes.listIngredients(recipe.id);
        setIngredients(recipeIngredients);
      } catch (error) {
        console.error("Error cargando ingredientes de receta:", error);
      }
    };

    // Cargar ingredientes disponibles siempre
    loadAvailableIngredients();

    if (recipe) {
      // Asegurar que todos los campos se carguen correctamente
      // Especialmente importante para InputNumber que necesita valores numéricos
      const formValues = {
        name: recipe.name ? String(recipe.name).trim() : "",
        code: recipe.code ? String(recipe.code).trim() : "",
        description: recipe.description
          ? String(recipe.description).trim()
          : "",
        branch: recipe.branch?.id || recipe.branch || activeBranch,
        recipe_type: recipe.recipe_type || "NUTRITIONAL_RECIPE",
        status: recipe.status || "ACTIVE",
        // Para total_yield_grams, convertir a número y preservar el valor (incluido 0)
        total_yield_grams:
          recipe.total_yield_grams !== undefined &&
          recipe.total_yield_grams !== null
            ? Number(recipe.total_yield_grams)
            : undefined,
      };
      console.log("Cargando receta para editar:", recipe);
      console.log("Valores del formulario a cargar:", formValues);
      console.log(
        "total_yield_grams como número:",
        formValues.total_yield_grams
      );

      // Resetear primero para limpiar valores anteriores
      form.resetFields();

      // Luego establecer los nuevos valores
      form.setFieldsValue(formValues);

      // Verificar que se cargaron correctamente
      setTimeout(() => {
        const loadedValues = form.getFieldsValue();
        console.log("Valores cargados en el formulario:", loadedValues);
      }, 100);

      loadRecipeIngredients();
    } else {
      form.resetFields();
      form.setFieldsValue({
        branch: activeBranch,
        recipe_type: "NUTRITIONAL_RECIPE",
        status: "ACTIVE",
      });
      setIngredients([]);
    }
    setCurrentStep(0);
  }, [visible, recipe, form, activeBranch]);

  const handleAddIngredient = () => {
    const newIngredient = {
      id: `temp_${Date.now()}`,
      ingredient: null,
      quantity: 0,
      unit: "g",
    };
    setIngredients([...ingredients, newIngredient]);
  };

  const handleRemoveIngredient = (id) => {
    setIngredients(ingredients.filter((ing) => ing.id !== id));
  };

  const handleIngredientChange = (id, field, value) => {
    setIngredients(
      ingredients.map((ing) =>
        ing.id === id ? { ...ing, [field]: value } : ing
      )
    );
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      try {
        await form.validateFields(["name", "branch", "total_yield_grams"]);
        setCurrentStep(1);
      } catch (error) {
        message.error("Por favor completa los campos requeridos");
      }
    } else if (currentStep === 1) {
      if (ingredients.length === 0) {
        message.warning("Debes agregar al menos un ingrediente");
        return;
      }
      setCurrentStep(2);
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Validar todos los campos del formulario antes de continuar
      try {
        await form.validateFields();
      } catch (error) {
        message.error("Por favor completa todos los campos requeridos");
        setLoading(false);
        return;
      }

      // Obtener valores del formulario - usar getFieldsValue con los nombres específicos
      // para asegurar que se capturen todos los valores
      const formValues = form.getFieldsValue([
        "name",
        "code",
        "description",
        "branch",
        "status",
        "total_yield_grams",
      ]);

      // Preparar datos de la receta
      // Asegurar que branch sea un valor válido (número o string)
      const branchValue = formValues.branch || activeBranch;

      // Preparar datos - usar directamente los valores del formulario
      // Si el formulario tiene valores, usarlos; si no, usar valores por defecto o de la receta original
      const recipeData = {
        name:
          formValues.name !== undefined && formValues.name !== null
            ? String(formValues.name).trim()
            : recipe
            ? recipe.name || ""
            : "",
        code:
          formValues.code !== undefined && formValues.code !== null
            ? String(formValues.code).trim()
            : recipe
            ? recipe.code || `REC-${Date.now()}`
            : `REC-${Date.now()}`,
        description:
          formValues.description !== undefined
            ? String(formValues.description || "").trim()
            : recipe
            ? recipe.description || ""
            : "",
        branch: branchValue,
        recipe_type: "NUTRITIONAL_RECIPE",
        status:
          formValues.status !== undefined
            ? formValues.status
            : recipe
            ? recipe.status || "ACTIVE"
            : "ACTIVE",
        // Para total_yield_grams, convertir a número si es válido
        total_yield_grams:
          formValues.total_yield_grams !== undefined &&
          formValues.total_yield_grams !== null
            ? Number(formValues.total_yield_grams)
            : recipe && recipe.total_yield_grams !== undefined
            ? Number(recipe.total_yield_grams)
            : null,
      };

      // Validar que los campos requeridos tengan valores
      if (!recipeData.name || recipeData.name.trim() === "") {
        message.error("El nombre de la receta es requerido");
        setLoading(false);
        return;
      }

      if (!recipeData.total_yield_grams || recipeData.total_yield_grams <= 0) {
        message.error("El rendimiento total debe ser mayor a 0");
        setLoading(false);
        return;
      }

      console.log(
        `${recipe ? "Actualizando" : "Creando"} receta con datos:`,
        recipeData
      );
      console.log("Valores del formulario (formValues):", formValues);
      console.log("Name en formValues:", formValues.name);
      console.log(
        "total_yield_grams en formValues:",
        formValues.total_yield_grams
      );
      console.log("recipeData.name:", recipeData.name);
      console.log(
        "recipeData.total_yield_grams:",
        recipeData.total_yield_grams
      );
      if (recipe) {
        console.log("Receta original:", recipe);
        console.log("Name original:", recipe.name);
        console.log("total_yield_grams original:", recipe.total_yield_grams);
      }

      // Crear o actualizar la receta
      let savedRecipe;
      try {
        if (recipe) {
          savedRecipe = await api.recipes.update(recipe.id, recipeData);
        } else {
          savedRecipe = await api.recipes.create(recipeData);
        }
        console.log("Receta guardada exitosamente:", savedRecipe);
        console.log("Branch de la receta guardada:", savedRecipe.branch);
        console.log("ActiveBranch actual:", activeBranch);
      } catch (error) {
        console.error("Error al guardar receta:", error);
        message.error(
          `Error al ${recipe ? "actualizar" : "crear"} la receta: ${
            error.message
          }`
        );
        setLoading(false);
        return;
      }

      // Verificar que la receta se guardó correctamente
      if (!savedRecipe || !savedRecipe.id) {
        throw new Error("La receta no se guardó correctamente");
      }

      // Guardar ingredientes
      const existingIngredients = recipe
        ? await api.recipes.listIngredients(savedRecipe.id)
        : [];

      // Eliminar ingredientes que ya no están
      for (const existing of existingIngredients) {
        if (!ingredients.find((ing) => ing.id === existing.id)) {
          try {
            await api.recipes.deleteIngredient(existing.id);
          } catch (error) {
            console.error(
              `Error eliminando ingrediente ${existing.id}:`,
              error
            );
            // Continuar aunque falle la eliminación de un ingrediente
          }
        }
      }

      // Crear/actualizar ingredientes
      const ingredientPromises = [];
      for (const ingredient of ingredients) {
        if (ingredient.ingredient && ingredient.quantity > 0) {
          const ingredientData = {
            recipe: savedRecipe.id,
            ingredient: ingredient.ingredient,
            quantity: ingredient.quantity,
            unit: ingredient.unit,
          };

          if (ingredient.id && ingredient.id.startsWith("temp_")) {
            // Nuevo ingrediente
            ingredientPromises.push(
              api.recipes.createIngredient(ingredientData).catch((error) => {
                console.error(`Error creando ingrediente:`, error);
                throw error;
              })
            );
          } else if (ingredient.id) {
            // Actualizar existente
            ingredientPromises.push(
              api.recipes
                .updateIngredient(ingredient.id, ingredientData)
                .catch((error) => {
                  console.error(
                    `Error actualizando ingrediente ${ingredient.id}:`,
                    error
                  );
                  throw error;
                })
            );
          }
        }
      }

      // Esperar a que todos los ingredientes se guarden
      if (ingredientPromises.length > 0) {
        await Promise.all(ingredientPromises);
        console.log("Todos los ingredientes se guardaron exitosamente");
      }

      // Verificar que todo se guardó correctamente
      const verifyRecipe = await api.recipes.retrieve(savedRecipe.id);
      if (!verifyRecipe) {
        throw new Error(
          "No se pudo verificar que la receta se guardó correctamente"
        );
      }

      message.success(
        `Receta ${recipe ? "actualizada" : "creada"} exitosamente`
      );
      form.resetFields();
      setIngredients([]);
      setCurrentStep(0);
      onSuccess();
    } catch (error) {
      console.error("Error guardando receta:", error);
      message.error(
        `Error al ${recipe ? "actualizar" : "crear"} receta: ${
          error.message || "Error desconocido"
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  const ingredientColumns = [
    {
      title: "Ingrediente",
      key: "ingredient",
      render: (_, record, index) => (
        <Select
          style={{ width: "100%" }}
          placeholder="Seleccionar ingrediente"
          value={record.ingredient}
          onChange={(value) =>
            handleIngredientChange(record.id, "ingredient", value)
          }
          showSearch
          optionFilterProp="label"
        >
          {availableIngredients.map((ing) => (
            <Select.Option key={ing.id} value={ing.id} label={ing.name}>
              {ing.name}{" "}
              {ing.supplier_company ? `(${ing.supplier_company})` : ""}
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Cantidad",
      key: "quantity",
      width: 150,
      render: (_, record) => (
        <InputNumber
          style={{ width: "100%" }}
          value={record.quantity}
          onChange={(value) =>
            handleIngredientChange(record.id, "quantity", value)
          }
          min={0}
          step={0.1}
        />
      ),
    },
    {
      title: "Unidad",
      key: "unit",
      width: 120,
      render: (_, record) => (
        <Select
          style={{ width: "100%" }}
          value={record.unit}
          onChange={(value) => handleIngredientChange(record.id, "unit", value)}
        >
          <Select.Option value="g">g</Select.Option>
          <Select.Option value="kg">kg</Select.Option>
          <Select.Option value="ml">ml</Select.Option>
          <Select.Option value="l">l</Select.Option>
          <Select.Option value="unidad">unidad</Select.Option>
        </Select>
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Popconfirm
          title="¿Eliminar este ingrediente?"
          onConfirm={() => handleRemoveIngredient(record.id)}
        >
          <Button type="link" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Modal
      title={recipe ? "Editar Receta Compleja" : "Nueva Receta Compleja"}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width="95%"
      style={{ maxWidth: 900 }}
      destroyOnHidden
    >
      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        <Step title="Datos Básicos" />
        <Step title="Ingredientes" />
        <Step title="Revisar" />
      </Steps>

      <Form form={form} layout="vertical">
        {currentStep === 0 && (
          <>
            <Form.Item
              name="name"
              label={
                <Space>
                  <span>¿Cómo se llama tu helado?</span>
                  <Tooltip title="Escribe el nombre de tu producto. Este nombre aparecerá en la etiqueta nutricional que pegarás en tus helados.">
                    <QuestionCircleOutlined style={{ color: "#1890ff" }} />
                  </Tooltip>
                </Space>
              }
              rules={[
                {
                  required: true,
                  message: "Necesitas poner un nombre para tu receta",
                },
              ]}
              extra={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Por ejemplo: "Helado de Leche Clásico" o "Helado de Vainilla"
                </Text>
              }
            >
              <Input placeholder="Ej: Helado de Leche Clásico" />
            </Form.Item>

            <Form.Item
              name="code"
              label={
                <Space>
                  <span>Código (opcional)</span>
                  <Tooltip title="Un código para identificar tu receta fácilmente. No es obligatorio, pero puede ayudarte a organizarte mejor.">
                    <QuestionCircleOutlined style={{ color: "#1890ff" }} />
                  </Tooltip>
                </Space>
              }
              extra={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Opcional. Por ejemplo: HEL-LEC-001
                </Text>
              }
            >
              <Input placeholder="Ej: HEL-LEC-001" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Descripción (opcional)"
              extra={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Puedes escribir una descripción breve de tu helado si quieres
                </Text>
              }
            >
              <TextArea
                rows={3}
                placeholder="Por ejemplo: Helado cremoso de leche tradicional..."
              />
            </Form.Item>

            <Form.Item
              name="branch"
              label="Sucursal"
              rules={[{ required: true }]}
            >
              <Select placeholder="Seleccionar sucursal">
                {branches.map((branch) => (
                  <Select.Option key={branch.value} value={branch.value}>
                    {branch.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="total_yield_grams"
              label={
                <Space>
                  <span>Rendimiento Total (gramos)</span>
                  <Tooltip title="El peso total en gramos del producto terminado después de preparar toda la receta. Este valor es crítico para calcular los valores nutricionales por 100g.">
                    <QuestionCircleOutlined style={{ color: "#1890ff" }} />
                  </Tooltip>
                </Space>
              }
              rules={[
                { required: true, message: "El rendimiento es requerido" },
              ]}
              extra={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Ejemplo: Si preparas una receta que produce 6.7 kg de helado,
                  ingresa 6700 gramos. El sistema usará este valor para calcular
                  los valores nutricionales por 100g del producto final.
                </Text>
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Ej: 6700"
                min={1}
                step={1}
              />
            </Form.Item>

            <Alert
              message="⚠️ Esto es muy importante: ¿Cuánto helado produce tu receta?"
              description="Cuando preparas toda tu receta completa, ¿cuántos kilos o gramos de helado obtienes al final? Por ejemplo, si produces 6.7 kilos de helado, escribe 6700 (en gramos). El sistema necesita saber esto para calcular correctamente cuántas calorías tiene cada 100 gramos de tu helado."
              type="info"
              icon={<InfoCircleOutlined />}
              style={{ marginBottom: 16 }}
              showIcon
            />
          </>
        )}

        {currentStep === 1 && (
          <>
            <Alert
              message="💡 Agrega los ingredientes de tu receta"
              description="Selecciona los ingredientes que registraste antes (leche, azúcar, crema, etc.) y escribe cuánto de cada uno usas en esta receta. El sistema sumará todas las calorías, proteínas y grasas de estos ingredientes para calcular la información nutricional de tu helado."
              type="info"
              icon={<InfoCircleOutlined />}
              style={{ marginBottom: 16 }}
              showIcon
            />

            <div style={{ marginBottom: 16 }}>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={handleAddIngredient}
                block
              >
                Agregar Ingrediente
              </Button>
            </div>

            <Table
              columns={ingredientColumns}
              dataSource={ingredients}
              rowKey="id"
              pagination={false}
              size="small"
            />

            {ingredients.length === 0 && (
              <Alert
                message="⚠️ No has agregado ingredientes todavía"
                description="Necesitas agregar al menos un ingrediente a tu receta para que el sistema pueda calcular la información nutricional. Haz clic en el botón 'Agregar Ingrediente' de arriba."
                type="warning"
                style={{ marginTop: 16 }}
                showIcon
              />
            )}
          </>
        )}

        {currentStep === 2 && (
          <div>
            <Alert
              message="Revisa tu receta antes de guardar"
              description={
                <div>
                  <p style={{ marginBottom: 8 }}>
                    <strong>Nombre del helado:</strong>{" "}
                    {form.getFieldValue("name") || "No especificado"}
                  </p>
                  <p style={{ marginBottom: 8 }}>
                    <strong>Cuánto produce tu receta:</strong>{" "}
                    {form.getFieldValue("total_yield_grams") ||
                      "No especificado"}{" "}
                    gramos
                  </p>
                  <p style={{ marginBottom: 0 }}>
                    <strong>Cantidad de ingredientes:</strong>{" "}
                    {ingredients.length}{" "}
                    {ingredients.length === 1 ? "ingrediente" : "ingredientes"}
                  </p>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Paragraph>
              Si todo está correcto, haz clic en "Crear Receta" para guardar.
              Luego podrás calcular la información nutricional.
            </Paragraph>
          </div>
        )}

        <Form.Item style={{ marginTop: 24 }}>
          <Space>
            {currentStep > 0 && <Button onClick={handlePrev}>Anterior</Button>}
            {currentStep < 2 ? (
              <Button type="primary" onClick={handleNext}>
                Siguiente
              </Button>
            ) : (
              <Button type="primary" onClick={handleSubmit} loading={loading}>
                {recipe ? "Actualizar" : "Crear"} Receta
              </Button>
            )}
            <Button onClick={onCancel}>Cancelar</Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RecipeForm;
